import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { PopularVehiclesService } from 'src/app/services/popular-vehicles/popular-vehicles.service';
import { PopularVehiclesModalService } from 'src/app/services/popular-vehicles-modal/popular-vehicles-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-popular-vehicles',
  templateUrl: './popular-vehicles.component.html',
  styleUrls: ['./popular-vehicles.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class PopularVehiclesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  popularVehicles: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedPopularVehicles: string[] = [];

  constructor(
    private _popularVehiclesService: PopularVehiclesService,
    private _popularVehiclesModalService: PopularVehiclesModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadPopularVehicles();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadPopularVehicles(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedPopularVehicles = [];
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._popularVehiclesService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.popularVehicles = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.popularVehicles = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        },
        error: (error) => {
          console.error('Error loading popular vehicles:', error);
          this.popularVehicles = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadPopularVehicles(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadPopularVehicles(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadPopularVehicles();
  }

  openAddModal(): void {
    this._popularVehiclesModalService.clearSelectedPopularVehicle();
    const modalElement = document.getElementById('kt_modal_popular_vehicle_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(popularVehicle: any): void {
    this._popularVehiclesModalService.setSelectedPopularVehicle(popularVehicle);
    const modalElement = document.getElementById('kt_modal_popular_vehicle_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deletePopularVehicle(popularVehicle: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete this popular vehicle. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(popularVehicle);
      }
    });
  }

  private performDelete(popularVehicle: any): void {
    const id = popularVehicle._id || popularVehicle.id;
    this.subscriptions.push(
      this._popularVehiclesService.DeleteDataById(id).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Popular vehicle has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadPopularVehicles(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete popular vehicle.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting popular vehicle.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onPopularVehicleAdded(): void {
    this.loadPopularVehicles(this.currentPage, this.searchTerm, this.filters);
  }

  onPopularVehicleUpdated(): void {
    this.loadPopularVehicles(this.currentPage, this.searchTerm, this.filters);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  isSelected(id: string): boolean {
    return this.selectedPopularVehicles.includes(id);
  }

  isAllSelected(): boolean {
    return this.popularVehicles.length > 0 && this.selectedPopularVehicles.length === this.popularVehicles.length;
  }

  isIndeterminate(): boolean {
    return this.selectedPopularVehicles.length > 0 && this.selectedPopularVehicles.length < this.popularVehicles.length;
  }

  toggleSelection(id: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedPopularVehicles.includes(id)) {
        this.selectedPopularVehicles.push(id);
      }
    } else {
      this.selectedPopularVehicles = this.selectedPopularVehicles.filter(item => item !== id);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedPopularVehicles = this.popularVehicles.map(item => item._id);
    } else {
      this.selectedPopularVehicles = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-popular-vehicle-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-popular-vehicle-table-toolbar="selected"]');

    if (this.selectedPopularVehicles.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedPopularVehicles(): void {
    if (this.selectedPopularVehicles.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedPopularVehicles.length} selected popular vehicle(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedPopularVehicles.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._popularVehiclesService.BulkDelete(this.selectedPopularVehicles).subscribe({
        next: () => {
          this.loadPopularVehicles();
          this.selectedPopularVehicles = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected popular vehicles have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting popular vehicles:', error);
          Swal.fire('Error!', 'Failed to delete selected popular vehicles.', 'error');
        }
      })
    );
  }

  getMakeName(make: any): string {
    if (!make) return 'N/A';
    return make.name || 'N/A';
  }

  getModelName(model: any): string {
    if (!model) return 'N/A';
    return model.name || 'N/A';
  }

  Math = Math;
}
